import styled from 'styled-components'
import { Autocomplete, IconButton, Popper, Typography } from '@mui/material'
import { colorPalette } from '../../../../style/colorPalette'
import { memo, useCallback, useMemo, useState } from 'react'
import { SelectOption } from '../../Kanban'
import ClearRoundedIcon from '@mui/icons-material/ClearRounded'
import TextInput from '../../../../components/editors/input/TextInput'

const SearchConditionAutocomplete = memo(
  ({
    value,
    options,
    onChange,
    onOpen,
    onClose,
    disabled,
    placeholder,
  }: {
    value?: string
    options: SelectOption[]
    onChange: (value?: string) => void
    onOpen?: () => void
    onClose?: () => void
    disabled?: boolean
    placeholder?: string
  }) => {
    const [inputValue, setInputValue] = useState('')

    const selectedOption = useMemo(
      () => options.find(o => o.value === value) ?? null,
      [options, value]
    )

    const handleChange = useCallback(
      (_: unknown, option: SelectOption | null) => {
        onChange(option?.value)
      },
      [onChange]
    )

    const handleInputChange = useCallback((_: unknown, newValue: string) => {
      setInputValue(newValue)
    }, [])

    const clear = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation()
        onChange(undefined)
        setInputValue('')
      },
      [onChange]
    )

    return (
      <StyledAutocomplete
        value={selectedOption}
        inputValue={inputValue}
        options={options}
        onChange={handleChange}
        onInputChange={handleInputChange}
        onOpen={onOpen}
        onClose={onClose}
        disabled={disabled}
        getOptionLabel={option => option.name}
        groupBy={option => option.groupBy ?? ''}
        isOptionEqualToValue={(option, val) => option.value === val.value}
        renderInput={params => (
          <TextInput
            {...params}
            variant="standard"
            placeholder={placeholder}
            InputProps={{
              ...params.InputProps,
              disableUnderline: true,
              endAdornment: value ? (
                <ClearButton onClick={clear} size="small">
                  <ClearRoundedIcon />
                </ClearButton>
              ) : (
                params.InputProps.endAdornment
              ),
            }}
          />
        )}
        renderOption={(props, option) => (
          <li {...props} key={option.value}>
            <OptionContent>
              <span>{option.name}</span>
              {option.subText && <SubText>{option.subText}</SubText>}
            </OptionContent>
          </li>
        )}
        PopperComponent={props => (
          <Popper {...props} placement="bottom-start" />
        )}
        slotProps={{
          paper: {
            sx: {
              '& .MuiAutocomplete-groupLabel': {
                fontWeight: 'bold',
                paddingTop: '10px',
                paddingBottom: '6px',
                lineHeight: '1.2',
                top: 0,
                backgroundColor: colorPalette.monotone[1],
              },
              '& .MuiAutocomplete-listbox': {
                paddingTop: 0,
              },
            },
          },
        }}
        noOptionsText=""
        size="small"
      />
    )
  },
  (prev, next) =>
    prev.value === next.value &&
    prev.options === next.options &&
    prev.onOpen === next.onOpen &&
    prev.onClose === next.onClose &&
    prev.disabled === next.disabled &&
    prev.placeholder === next.placeholder
)

export default SearchConditionAutocomplete

const StyledAutocomplete = styled(Autocomplete<SelectOption>)({
  minWidth: '250px',
  backgroundColor: colorPalette.monotone[0],
  border: `1px solid ${colorPalette.monotone[2]}`,
  borderRadius: '4px',
  '& .MuiInputBase-root': {
    padding: '4px 8px',
    minHeight: '32px',
  },
  '& .MuiAutocomplete-input': {
    padding: '0 !important',
  },
  '& .MuiTypography-root': {
    lineHeight: '20px !important',
    color: colorPalette.monotone[5],
  },
})

const ClearButton = styled(IconButton)({
  padding: '2px',
})

const OptionContent = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
})

const SubText = styled(Typography)({
  fontSize: '11px',
  color: colorPalette.monotone[3],
  lineHeight: '1 !important',
})
